import React, { useEffect, useState, useRef } from "react";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { FaRegEdit } from "react-icons/fa";
import { RiImageAddFill } from "react-icons/ri";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Input } from "antd";
import { FilterMatchMode } from "primereact/api";
import { IoIosSearch } from "react-icons/io";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { useEvents } from "../hooks/useEvent";
import { Calendar } from "primereact/calendar";
import { convertDate, convertTime } from "../helper";
import OverlappingImages from "../components/overlappingImages";
import FileUploadDialog from "../components/FileUpload/FileUploadDialog";
import { motion, AnimatePresence } from "framer-motion";
import TinyMCEEditor from "../components/QuillEditor";
import LocationDropdowns from "../components/LocationDropdowns";
import { truncateText } from "../utils/authHelpers";
import { renderFieldLabel } from "../utils/formHelpers";
import { HideSortIcons } from "../utils/tableStyles";

export const EventManagement = () => {
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState("next");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const {
    events,
    createEvent,
    createEventLoading,
    isLoading,
    error,
    addEventImages,
    addEventImagesLoading,
    updateEvent,
    updateEventLoading,
    deleteEvent,
    deleteEventLoading,
    createEventSuccess,
    addEventImagesSuccess,
    updateEventSuccess,
    addEventImagesReset,
  } = useEvents();
  const [selectedEvent, setSelectedEvent] = useState({
    id: null,
    title: "",
    description: "",
    dates: "",
    time: "",
    state: "",
    city: "",
    zip_code: "",
    images: [],
  });
  const [eventId, setEventId] = useState(null);
  const [visibleRight, setVisibleRight] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const eventFormik = useFormik({
    initialValues: {
      title: "",
      description: "",
      dates: "",
      time: "",
      country: "United States",
      county: "",
      state: "",
      street_name: "",
      street_number: "",
      compass_direction: "",
      post_street_direction: "",
      unit_number: "",
      zip_code: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
      dates: Yup.date().required("Dates is required"),
      time: Yup.string().required("Time is required"),
      // Make location fields optional for now to test form submission
      country: Yup.string().optional(),
      county: Yup.string().optional(),
      state: Yup.string().optional(),
      street_name: Yup.string().optional(),
      street_number: Yup.string().optional(),
      compass_direction: Yup.string().optional(),
      post_street_direction: Yup.string().optional(),
      unit_number: Yup.string().optional(),
      zip_code: Yup.string().optional(),
    }),
    onSubmit: (values) => {
      createEvent(values);
    },
  });

  const updateEventFormik = useFormik({
    initialValues: {
      title: "",
      description: "",
      dates: "",
      time: "",
      country: "United States",
      county: "",
      state: "",
      street_name: "",
      street_number: "",
      compass_direction: "",
      post_street_direction: "",
      unit_number: "",
      zip_code: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      description: Yup.string().required("Description is required"),
      dates: Yup.date().required("Dates is required"),
      time: Yup.string().required("Time is required"),
      // Make location fields optional for now to test form submission
      country: Yup.string().optional(),
      county: Yup.string().optional(),
      state: Yup.string().optional(),
      street_name: Yup.string().optional(),
      street_number: Yup.string().optional(),
      compass_direction: Yup.string().optional(),
      post_street_direction: Yup.string().optional(),
      unit_number: Yup.string().optional(),
      zip_code: Yup.string().optional(),
    }),
    onSubmit: (values) => {
      updateEvent({ eventId: selectedEvent.id, eventData: values });
    },
  });

  const confirm2 = (eventId) => {
    setDeleteTargetId(eventId);
    setDeleteDialogVisible(true);
  };

  const handleDelete = async () => {
    await deleteEvent(deleteTargetId);
    setDeleteDialogVisible(false);
    setDeleteTargetId(null);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <div className="flex items-end justify-end">
        <Input
          placeholder="Search"
          className="text-sm focus:ring-0 focus:outline-none"
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          prefix={<IoIosSearch />}
        />
      </div>
    );
  };

  const actionBodyTemplateForFirstColumn = (rowData) => {
    return (
      <div className="flex gap-5">
        <FaRegEdit
          onClick={() => {
            setVisibleRight(true);
            setSelectedEvent({
              id: rowData.id,
              title: rowData.title,
              description: rowData.description,
              dates: rowData.date,
              time: rowData.time,
              country: rowData.country || "United States",
              county: rowData.county || "",
              state: rowData.state || "",
              street_name: rowData.street_name || "",
              street_number: rowData.street_number || "",
              compass_direction: rowData.compass_direction || "",
              post_street_direction: rowData.post_street_direction || "",
              unit_number: rowData.unit_number || "",
              zip_code: rowData.zip_code || "",
              city: rowData.city,
              coordinates: rowData.coordinates,
              images: rowData.images,
            });
          }}
          className="text-[#003049] hover:text-[#002f499e] cursor-pointer"
          size={24}
        />
      </div>
    );
  };
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-5">
        <RiImageAddFill
          onClick={() => {
            setVisible2((prev) => !prev);
            setEventId(rowData.id);
          }}
          size={24}
          className=" hover:text-cyan-950 cursor-pointer"
        />
        <MdOutlineDeleteOutline
          onClick={() => confirm2(rowData.id)}
          size={24}
          className="text-red-600 hover:text-red-500 cursor-pointer"
        />
      </div>
    );
  };
  
  const countBodyTemplate = (rowData) => {
    return events.indexOf(rowData) + 1;
  };
  const imageBodyTemplate = (rowData) => {
    return (
      <div className="relative">
        {rowData.images && rowData.images.length > 0 ? (
          <OverlappingImages images={rowData?.images} />
        ) : (
          <p className="whitespace-nowrap">NO IMAGE</p>
        )}
      </div>
    );
  };

  const header = renderHeader();
  const customHeader = () => (
    <div className="flex items-start justify-start">
      <h1 className="font-semibold">Update event's information</h1>
    </div>
  );

  const discriptionBodyTemplate = (rowData) => {
    const maxLength = 25;
    const description = rowData.description;
    return (
      <span
        dangerouslySetInnerHTML={{
          __html:
            description.length > maxLength
              ? `${description.substring(0, maxLength)}...`
              : description,
        }}
        className="text-sm"
      ></span>
    );
  };

  const dateBodyTemplate = (rowData) => {
    const date = new Date(rowData.date);
    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <div className="flex items-center gap-2 whitespace-nowrap">
        {formattedDate}
      </div>
    );
  };

  const timeBodyTemplate = (rowData) => {
    const time = new Date(rowData.time);
    const formattedTime = time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return (
      <div className="flex items-center gap-2 whitespace-nowrap">
        {formattedTime}
      </div>
    );
  };

  useEffect(() => {
    if (selectedEvent && selectedEvent.id !== null) {
      updateEventFormik.setValues({
        title: selectedEvent.title || "",
        description: selectedEvent.description || "",
        dates: selectedEvent.dates ? new Date(selectedEvent.dates) : "",
        time: selectedEvent.time ? new Date(selectedEvent.time) : "",
        country: selectedEvent.country || "US", // Use ISO code for country
        county: selectedEvent.county || "",
        state: selectedEvent.state || "",
        street_name: selectedEvent.street_name || "",
        street_number: selectedEvent.street_number || "",
        compass_direction: selectedEvent.compass_direction || "",
        post_street_direction: selectedEvent.post_street_direction || "",
        unit_number: selectedEvent.unit_number || "",
        zip_code: selectedEvent.zip_code || "",
      });
    }
  }, [selectedEvent]); // Depend on the entire selectedEvent object

  useEffect(() => {
    if (createEventSuccess) {
      setVisible(false);
      eventFormik.resetForm();
    }
    if (updateEventSuccess) {
      setVisibleRight(false);
      updateEventFormik.resetForm();
    }
  }, [createEventSuccess, updateEventSuccess]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
    exit: (direction) => ({
      x: direction > 0 ? "-100%" : "100%",
      transition: { duration: 0.5 },
    }),
  };

  const showImage = (newIndex, newDirection) => {
    setDirection(newDirection);
    if (newIndex < 0) {
      setCurrentIndex(selectedEvent.images.length - 1);
    } else if (newIndex >= selectedEvent.images.length) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex(newIndex);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      showImage(currentIndex + 1, "next");
    }, 3000);
    return () => clearInterval(interval);
  }, [currentIndex, selectedEvent.images.length]);
  if (isLoading)
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );
  if (error) return <p>Error: {error.message}</p>;

  return (
    <>
      <HideSortIcons />
      <div className="pt-5 px-4">
        <div className="flex justify-between">
          <h1 className="text-[24px] text-[#003049] font-semibold">Calendar</h1>
          <div>
            <Button
              label="Add event"
              className="bg-[#BA1500] text-white btn text-sm focus:outline-none focus:ring-0"
              onClick={() => setVisible(true)}
              icon="pi pi-plus"
            />
          </div>
        </div>
        <div className="bg-white rounded-md mt-5 text-[18px]">
          <div className="card">
            <DataTable
              value={events}
              paginator
              rows={10}
              tableStyle={{ minWidth: "50rem", fontSize: "14px" }}
              header={header}
              globalFilter={filters.global.value}
              emptyMessage="No event found."
              filters={filters}
              sortMode="single"
              showSortIcon={false}
              className="text-xs sm:text-sm md:text-base"
              rowClassName={(index) => {
                return {
                  "bg-gray-100": index + (1 % 2) === 0,
                };
              }}
              onRowDoubleClick={(rowData) => {
                setVisibleRight(true);
                setSelectedEvent({
                  id: rowData.id,
              title: rowData.title,
              description: rowData.description,
              dates: rowData.date,
              time: rowData.time,
              country: rowData.country || "United States",
              county: rowData.county || "",
              state: rowData.state || "",
              street_name: rowData.street_name || "",
              street_number: rowData.street_number || "",
              compass_direction: rowData.compass_direction || "",
              post_street_direction: rowData.post_street_direction || "",
              unit_number: rowData.unit_number || "",
              zip_code: rowData.zip_code || "",
              city: rowData.city,
              coordinates: rowData.coordinates,
              images: rowData.images,
                });
              }}
            >
              <Column
                header="Action"
                headerClassName="bg-[#003049] text-white"
                style={{ width: "10%" }}
                body={actionBodyTemplateForFirstColumn}
              ></Column>
              <Column
                header="#"
                headerClassName="bg-[#003049] text-white"
                style={{ width: "10%" }}
                body={countBodyTemplate}
              ></Column>
              <Column
                field="title"
                header="Title"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap max-w-[120px] truncate"
                style={{ width: "25%" }}
                body={(rowData) => (
                  <span title={rowData.title}>
                    {truncateText(rowData.title, 25)}
                  </span>
                )}
                sortable
              ></Column>
              <Column
                field="imges"
                header="Images"
                headerClassName="bg-[#003049] text-white"
                style={{ width: "25%" }}
                body={imageBodyTemplate}
              ></Column>
              <Column
                field="county"
                header="County"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap max-w-[80px] truncate"
                style={{ width: "25%" }}
                body={(rowData) => (
                  <span title={rowData.county}>
                    {truncateText(rowData.county, 15)}
                  </span>
                )}
                sortable
              ></Column>
              <Column
                field="state"
                header="State"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap max-w-[60px] truncate"
                style={{ width: "25%" }}
                body={(rowData) => (
                  <span title={rowData.state}>
                    {truncateText(rowData.state, 10)}
                  </span>
                )}
                sortable
              ></Column>
              <Column
                field="street_name"
                header="Street Name"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap max-w-[100px] truncate"
                style={{ width: "25%" }}
                body={(rowData) => (
                  <span title={rowData.street_name}>
                    {truncateText(rowData.street_name, 15)}
                  </span>
                )}
                sortable
              ></Column>
              <Column
                field="street_number"
                header="Street Number"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap"
                style={{ width: "25%" }}
                body={(rowData) => rowData.street_number || "N/A"}
                sortable
              ></Column>
              <Column
                field="unit_number"
                header="unit #"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap"
                style={{ width: "25%" }}
                body={(rowData) => rowData.unit_number || "N/A"}
                sortable
              ></Column>
              <Column
                field="description"
                header="Description"
                headerClassName="bg-[#003049] text-white"
                className="whitespace-nowrap max-w-[160px] truncate"
                style={{ width: "25%" }}
                body={discriptionBodyTemplate}
                sortable
              ></Column>
              <Column
                field="time"
                header="Time"
                headerClassName="bg-[#003049] text-white"
                style={{ width: "25%" }}
                body={timeBodyTemplate}
                sortable
              ></Column>
              <Column
                field="date"
                header="date"
                headerClassName="bg-[#003049] text-white"
                style={{ width: "25%" }}
                body={dateBodyTemplate}
                sortable
              ></Column>
              <Column
                field="zip_code"
                header="Zip Code"
                headerClassName="bg-[#003049] text-white whitespace-nowrap"
                style={{ width: "25%" }}
                sortable
              ></Column>
              <Column
                header="Action"
                headerClassName="bg-[#003049] text-white"
                style={{ width: "10%" }}
                body={actionBodyTemplate}
              ></Column>
            </DataTable>
            {/* Update events  */}
            <Sidebar
              visible={visibleRight}
              position="right"
              onHide={() => setVisibleRight(false)}
              style={{ width: "30rem" }}
              header={customHeader}
            >
              <div className="mt-5">
                <form
                  onSubmit={(e) => {
                    updateEventFormik.handleSubmit(e);
                  }}
                >
                  <div className="relative w-full h-80 sm:h-80 md:h-80 lg:h-[20rem] mb-5 overflow-hidden">
                    <AnimatePresence initial={false} custom={direction}>
                      <motion.div
                        key={currentIndex}
                        className="absolute w-full h-full"
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        custom={direction}
                      >
                        <img
                          src={selectedEvent.images[currentIndex]?.image_url}
                          alt={`Event ${currentIndex + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />

                        {/* Overlay Text */}
                        <div className="absolute bottom-0 w-full bg-gradient-to-t from-black/60 to-transparent text-white p-4">
                          <h2 className="text-xl font-semibold">
                            {selectedEvent?.title}
                          </h2>
                          <p
                            className="text-sm"
                            dangerouslySetInnerHTML={{
                              __html: selectedEvent?.description
                                ? selectedEvent.description.length > 100
                                  ? `${selectedEvent.description.substring(
                                      0,
                                      100
                                    )}...`
                                  : selectedEvent.description
                                : "",
                            }}
                          ></p>
                          <div className="flex items-center justify-between">
                            <p className="text-sm">
                              {convertDate(selectedEvent?.dates)}
                            </p>
                            <p className="text-sm">
                              {convertTime(selectedEvent?.time)}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  <div className="py-2 my-2">
                    <FloatLabel>
                      <InputText
                        id="title"
                        name="title"
                        type="text"
                        className="w-full border border-gray-500 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-100 "
                        onChange={updateEventFormik.handleChange}
                        onBlur={updateEventFormik.handleBlur}
                        value={updateEventFormik.values.title}
                      />
                      <label htmlFor="title" className="text-sm">
                        Title <span className="text-red-500 ml-1">*</span>
                      </label>
                    </FloatLabel>
                    {updateEventFormik.touched.title &&
                      updateEventFormik.errors.title && (
                        <div
                          className="text-[#BA1500] text-sm"
                          aria-live="polite"
                        >
                          {updateEventFormik.errors.title}
                        </div>
                      )}
                  </div>
                  <div className="py-2 my-2">
                    <FloatLabel>
                      <TinyMCEEditor
                        value={updateEventFormik.values.description}
                        onChange={(val) =>
                          updateEventFormik.setFieldValue("description", val)
                        }
                      />
                    </FloatLabel>
                    {updateEventFormik.touched.description &&
                      updateEventFormik.errors.description && (
                        <div
                          className="text-[#BA1500] text-sm"
                          aria-live="polite"
                        >
                          {updateEventFormik.errors.description}
                        </div>
                      )}
                  </div>
                  <div className="grid grid-flow-col grid-cols-2 sm:gap-2">
                    <div>
                      <Calendar
                        style={{
                          width: "100%",
                        }}
                        id="dates"
                        name="dates"
                        value={updateEventFormik.values.dates}
                        onChange={(e) =>
                          updateEventFormik.setFieldValue("dates", e.value)
                        }
                        onBlur={updateEventFormik.handleBlur}
                        placeholder="Date"
                      />

                      {updateEventFormik.touched.dates &&
                        updateEventFormik.errors.dates && (
                          <div
                            className="text-[#BA1500] text-sm"
                            aria-live="polite"
                          >
                            {updateEventFormik.errors.dates}
                          </div>
                        )}
                    </div>
                    <div>
                      <Calendar
                        style={{
                          width: "100%",
                        }}
                        id="time"
                        name="time"
                        value={updateEventFormik.values.time}
                        onBlur={updateEventFormik.handleBlur}
                        onChange={(e) =>
                          updateEventFormik.setFieldValue("time", e.value)
                        }
                        timeOnly
                        placeholder="Time"
                      />

                      {updateEventFormik.touched.time &&
                        updateEventFormik.errors.time && (
                          <div
                            className="text-[#BA1500] text-sm"
                            aria-live="polite"
                          >
                            {updateEventFormik.errors.time}
                          </div>
                        )}
                    </div>
                  </div>
                  {/* Location Dropdowns */}
                  <LocationDropdowns
                    formik={updateEventFormik}
                    showStreetName={true}
                    showStreetNumber={true}
                    showCompassDirection={true}
                    showPostStreetDirection={true}
                    showUnitNumber={true}
                    showZipCode={true}
                  />
                  <button
                    type="submit"
                    className="w-full bg-[#BA1500] text-center text-white py-3 disabled:opacity-50"
                    disabled={updateEventLoading}
                  >
                    {updateEventLoading ? (
                      <i
                        className="pi pi-spin pi-spinner text-white"
                        style={{ fontSize: "2rem" }}
                      ></i>
                    ) : (
                      "Update"
                    )}
                  </button>
                </form>
              </div>
            </Sidebar>
            <Dialog
              header="Add Events"
              visible={visible}
              className="w-full lg:w-[50%]"
              onHide={() => {
                setVisible(false);
                eventFormik.resetForm();
              }}
            >
              <form
                onSubmit={(e) => {
                  eventFormik.handleSubmit(e);
                }}
              >
                <div className="py-2 my-2">
                  <FloatLabel>
                    <InputText
                      id="title"
                      name="title"
                      type="text"
                      className="w-full border border-gray-500 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-100 "
                      onChange={eventFormik.handleChange}
                      onBlur={eventFormik.handleBlur}
                      value={eventFormik.values.title}
                    />
                    <label htmlFor="title" className="text-sm">
                      Title <span className="text-red-500 ml-1">*</span>
                    </label>
                  </FloatLabel>
                  {eventFormik.touched.title && eventFormik.errors.title && (
                    <div className="text-[#BA1500] text-sm" aria-live="polite">
                      {eventFormik.errors.title}
                    </div>
                  )}
                </div>
                <div className="py-2 my-2">
                  <FloatLabel>
                    <TinyMCEEditor
                      value={eventFormik.values.description}
                      onChange={(val) => {
                        eventFormik.setFieldValue("description", val);
                      }}
                      onBlur={() =>
                        eventFormik.setFieldTouched("description", true)
                      }
                    />
                  </FloatLabel>
                  {eventFormik.touched.description &&
                    eventFormik.errors.description && (
                      <div
                        className="text-[#BA1500] text-sm"
                        aria-live="polite"
                      >
                        {eventFormik.errors.description}
                      </div>
                    )}
                </div>
                <div className="grid grid-flow-col grid-cols-2 sm:gap-2">
                  <div>
                    <Calendar
                      style={{
                        width: "100%",
                      }}
                      id="dates"
                      name="dates"
                      value={eventFormik.values.dates}
                      onChange={(e) =>
                        eventFormik.setFieldValue("dates", e.value)
                      }
                      onBlur={eventFormik.handleBlur}
                      placeholder="Date"
                    />

                    {eventFormik.touched.dates && eventFormik.errors.dates && (
                      <div
                        className="text-[#BA1500] text-sm"
                        aria-live="polite"
                      >
                        {eventFormik.errors.dates}
                      </div>
                    )}
                  </div>
                  <div>
                    <Calendar
                      style={{
                        width: "100%",
                      }}
                      id="time"
                      name="time"
                      value={eventFormik.values.time}
                      onBlur={eventFormik.handleBlur}
                      onChange={(e) =>
                        eventFormik.setFieldValue("time", e.value)
                      }
                      timeOnly
                      hourFormat="12"
                      placeholder="Time"
                    />

                    {eventFormik.touched.time && eventFormik.errors.time && (
                      <div
                        className="text-[#BA1500] text-sm"
                        aria-live="polite"
                      >
                        {eventFormik.errors.time}
                      </div>
                    )}
                  </div>
                </div>
                {/* Location Dropdowns */}
                <LocationDropdowns
                  formik={eventFormik}
                  showStreetName={true}
                  showStreetNumber={true}
                  showCompassDirection={true}
                  showPostStreetDirection={true}
                  showUnitNumber={true}
                  showZipCode={true}
                />
                <button
                  type="submit"
                  className="w-full bg-[#BA1500] text-center text-white py-3 disabled:opacity-50"
                  disabled={createEventLoading}
                >
                  {createEventLoading ? (
                    <i
                      className="pi pi-spin pi-spinner text-white"
                      style={{ fontSize: "2rem" }}
                    ></i>
                  ) : (
                    "Save"
                  )}
                </button>
              </form>
            </Dialog>
            <FileUploadDialog
              visible={visible2}
              onClose={() => setVisible2(false)}
              selectedEvent={eventId}
              addEventImages={addEventImages}
              addEventImagesLoading={addEventImagesLoading}
              addEventImagesSuccess={addEventImagesSuccess}
              addEventImagesReset={addEventImagesReset}
            />
          </div>
        </div>

        {/* Custom Delete Dialog */}
        <Dialog
          header="Delete Confirmation"
          visible={deleteDialogVisible}
          style={{ width: "350px" }}
          onHide={() => setDeleteDialogVisible(false)}
          footer={
            <div className="flex justify-end gap-3">
              <Button
                label="No"
                icon="pi pi-times"
                onClick={() => setDeleteDialogVisible(false)}
                className="bg-gray-300 text-gray-700 p-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-0"
                disabled={deleteEventLoading}
              />
              <Button
                label="Yes"
                icon="pi pi-check"
                onClick={handleDelete}
                className="bg-red-500 text-white p-2 rounded-md hover:bg-red-600 focus:outline-none focus:ring-0"
                disabled={deleteEventLoading}
                autoFocus
              />
              {deleteEventLoading && (
                <i
                  className="pi pi-spin pi-spinner text-red-500 ml-2"
                  style={{ fontSize: "1.5rem" }}
                ></i>
              )}
            </div>
          }
          modal
        >
          <span>Do you want to delete this event?</span>
        </Dialog>
      </div>
    </>
  );
};
